import { useCallback, useEffect, useMemo, useRef } from 'react';
import { env } from 'constants/env';
import {
  CaptureClient,
  FetchConsentCaptureApi,
  RrwebEngine,
  type HeadersProvider,
} from '@clearcover/consent-capture-sdk/rrweb-engine';
import { credentialedFetch } from './consentCaptureFetch';
import { FullStory } from 'V2/Analytics';
import { LogToService } from 'V2/Utilities/Logger';

type UseConsentCaptureParams = {
  appId: string;
  enabled?: boolean;
  sdkVersion?: string;
  chunkSize?: number;
  flushIntervalMs?: number;
  maxBufferedEvents?: number;
  maskAllInputs?: boolean;
};

type UseConsentCaptureResult = {
  finalizeOnSubmit: () => Promise<void>;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
};

const logConsentCaptureError = (thread: string, error: unknown) => {
  const message = getErrorMessage(error);
  LogToService.captureException({
    errorType: 'client',
    message,
    thread,
    error: error instanceof Error ? error : new Error(message),
  });
};

export const useConsentCapture = ({
  appId,
  enabled,
  sdkVersion,
  chunkSize,
  flushIntervalMs,
  maxBufferedEvents,
  maskAllInputs = false,
}: UseConsentCaptureParams): UseConsentCaptureResult => {
  const clientRef = useRef<CaptureClient | null>(null);
  const headersProvider = useCallback<HeadersProvider>(async () => {
    try {
      const authTokenFromLocalStorage = localStorage.getItem('authToken');

      const headers: Record<string, string> = {
        'Clearcover-Fullstory-Identity': FullStory.getCurrentSession() || '',
        'Clearcover-ClickId': sessionStorage.getItem('clickId') || '',
      };
      const applicantToken = sessionStorage.getItem('authToken');
      if (authTokenFromLocalStorage) {
        headers.Authorization = `Bearer ${authTokenFromLocalStorage}`;
      } else if (applicantToken) {
        // DLC-1725: applicant auth is the applicant_auth cookie. Only send the legacy header while a
        // token exists — reznor decodes the header whenever present, so an empty value fails the request.
        headers['Clearcover-Applicant-Authorization-Token'] = applicantToken;
      }

      const awsWafToken: string | undefined =
        (window as any).AwsWafIntegration &&
        (await (window as any).AwsWafIntegration.getToken());

      if (awsWafToken) {
        headers['X-Aws-Waf-Token'] = awsWafToken;
      }

      return headers;
    } catch (error) {
      logConsentCaptureError('Aws-Waf-Token', error);
      return {};
    }
  }, []);

  const finalizeOnSubmit = useCallback(async () => {
    await clientRef.current?.finalize();
  }, []);

  useEffect(() => {
    if (!enabled || !appId) return;

    const client = new CaptureClient({
      appId,
      // Still required alongside `api`: CaptureClient builds the pagehide/visibilitychange beacon URL
      // from apiBaseUrl directly instead of going through the api instance.
      apiBaseUrl: env.apiHost,
      // The client only falls back to its own FetchConsentCaptureApi when `api` is absent, so the
      // top-level headersProvider would be dead config here — the api instance owns the headers.
      api: new FetchConsentCaptureApi({
        apiBaseUrl: env.apiHost,
        headersProvider,
        fetchImpl: credentialedFetch,
      }),
      enabled,
      sdkVersion,
      chunkSize,
      flushIntervalMs,
      maxBufferedEvents,
      engine: new RrwebEngine({ maskAllInputs }),
      logger: logConsentCaptureError,
    });
    clientRef.current = client;
    client.start();

    const onPageHide = () => {
      client.flushOnUnload();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        client.flushOnUnload();
      }
    };

    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      client.dispose();
      if (clientRef.current === client) {
        clientRef.current = null;
      }
    };
  }, [
    appId,
    chunkSize,
    enabled,
    flushIntervalMs,
    headersProvider,
    maskAllInputs,
    maxBufferedEvents,
    sdkVersion,
  ]);

  return useMemo(
    () => ({
      finalizeOnSubmit,
    }),
    [finalizeOnSubmit]
  );
};
